import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, Sparkles, Star, ShieldCheck, Clock } from 'lucide-react';
import { submitWaitlistApplication } from '../services/apiClient';

function useCountdown(targetIso) {
  const target = useMemo(() => (targetIso ? new Date(targetIso).getTime() : null), [targetIso]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!target || Number.isNaN(target)) return null;
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds, done: diff <= 0 };
}

function CountdownCell({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="w-16 sm:w-20 py-3 rounded-2xl border border-white/15 bg-white/[0.04] backdrop-blur text-center">
        <span className="font-display font-bold text-2xl sm:text-3xl text-white tabular-nums">
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className="mt-1.5 text-[10px] uppercase tracking-widest text-white/60 font-display">{label}</span>
    </div>
  );
}

const initialForm = { firstName: '', lastName: '', email: '', educationAcknowledged: false, marketingConsent: true, website: '' };

export default function LaunchCelebrationPage({ siteMode, onSignIn }) {
  const celebration = siteMode?.celebration || {};
  const countdown = useCountdown(celebration.showCountdown ? celebration.launchTargetIso : null);

  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const startedAtRef = useRef(null);
  useEffect(() => { startedAtRef.current = Date.now(); }, []);

  const headline = celebration.headline || `Celebrating America's ${celebration.americaYears || 250}th Birthday`;
  const subMessage = celebration.subMessage
    || 'Bond Mentor reads the bond market and turns its warnings into plain-English market awareness. Join the vetted early-access list and be first in line at launch.';

  function update(name, value) { setForm((c) => ({ ...c, [name]: value })); }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setStatus(null);
    const result = await submitWaitlistApplication({
      ...form,
      investorType: 'individual_investor',
      investorExperience: 'beginner',
      bondKnowledge: 'novice',
      heardFrom: 'July 4th Launch Page',
      interestReason: 'Joined via the launch celebration page.',
      termsAccepted: true,
      elapsedMs: Date.now() - (startedAtRef.current || Date.now()),
    });
    if (result?.ok) {
      setStatus({ type: 'success', message: result.message || 'You are on the Bond Mentor early-access list. Watch your inbox for your invite.' });
      setForm(initialForm);
    } else {
      setStatus({ type: 'error', message: result?.message || 'Could not submit right now. Please try again.' });
    }
    setSubmitting(false);
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-[#0a1730] via-mentor-bg to-[#1a0a12] text-white">
      <style>{`
        @keyframes bmFirework {
          0% { transform: scale(0); opacity: 0; }
          12% { opacity: 1; }
          55% { opacity: 0.9; }
          100% { transform: scale(1); opacity: 0; }
        }
        @keyframes bmWave {
          0%, 100% { transform: perspective(300px) rotateY(-6deg) skewX(-1.5deg); }
          50% { transform: perspective(300px) rotateY(6deg) skewX(1.5deg); }
        }
        .bm-fw { position: absolute; width: 8px; height: 8px; border-radius: 50%; opacity: 0; transform: scale(0); }
        .bm-fw::before, .bm-fw::after { content: ''; position: absolute; inset: 0; border-radius: 50%;
          box-shadow: 0 -28px currentColor, 0 28px currentColor, -28px 0 currentColor, 28px 0 currentColor,
            20px 20px currentColor, -20px 20px currentColor, 20px -20px currentColor, -20px -20px currentColor; }
        .bm-flag { animation: bmWave 4s ease-in-out infinite; transform-origin: left center; }
        @media (prefers-reduced-motion: reduce) { .bm-fw, .bm-flag { animation: none !important; } }
      `}</style>

      {/* Festive accent glows */}
      <div className="pointer-events-none absolute -top-24 -left-24 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl" />
      <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-red-600/20 blur-3xl" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-red-600 via-white/70 to-blue-600" />

      {/* Fireworks */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <span className="bm-fw" style={{ top: '14%', left: '16%', color: '#ef4444', animation: 'bmFirework 2.6s ease-out 0.2s infinite' }} />
        <span className="bm-fw" style={{ top: '10%', left: '78%', color: '#60a5fa', animation: 'bmFirework 2.9s ease-out 1.1s infinite' }} />
        <span className="bm-fw" style={{ top: '22%', left: '50%', color: '#fde68a', animation: 'bmFirework 3.2s ease-out 0.7s infinite' }} />
        <span className="bm-fw" style={{ top: '8%', left: '36%', color: '#f8fafc', animation: 'bmFirework 2.8s ease-out 1.8s infinite' }} />
        <span className="bm-fw" style={{ top: '18%', left: '88%', color: '#ef4444', animation: 'bmFirework 3.1s ease-out 2.3s infinite' }} />
        <span className="bm-fw" style={{ top: '26%', left: '8%', color: '#60a5fa', animation: 'bmFirework 3.0s ease-out 1.5s infinite' }} />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center">
          <div className="flex justify-center mb-5" aria-hidden="true">
            <svg className="bm-flag" width="92" height="58" viewBox="0 0 76 48" xmlns="http://www.w3.org/2000/svg" style={{ filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.45))', borderRadius: '4px' }}>
              <defs>
                <clipPath id="flagRound"><rect width="76" height="48" rx="3" /></clipPath>
              </defs>
              <g clipPath="url(#flagRound)">
                {[...Array(13)].map((_, i) => (
                  <rect key={i} x="0" y={(48 / 13) * i} width="76" height={48 / 13} fill={i % 2 === 0 ? '#b22234' : '#ffffff'} />
                ))}
                <rect x="0" y="0" width="34" height={(48 / 13) * 7} fill="#3c3b6e" />
                {[...Array(5)].map((_, r) => (
                  [...Array(6)].map((_, c) => (
                    <circle key={`${r}-${c}`} cx={3 + c * 5.4} cy={2.6 + r * 4.6} r="1" fill="#ffffff" />
                  ))
                ))}
              </g>
            </svg>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/[0.05] px-4 py-1.5 text-xs font-display font-bold uppercase tracking-widest text-white/80 mb-6">
            <Star size={14} className="text-yellow-300" /> {celebration.americaHeadline || '250th America\u2019s Independence'} <Star size={14} className="text-yellow-300" />
          </div>
          <h1 className="font-display font-black text-4xl sm:text-6xl leading-tight mb-4">
            {headline}
          </h1>
          <p className="max-w-2xl mx-auto text-white/75 leading-relaxed text-lg mb-8">
            {subMessage}
          </p>

          {countdown && (
            <div className="mb-4">
              {countdown.done ? (
                <p className="font-display font-bold text-2xl text-yellow-300">Bond Mentor is live — welcome!</p>
              ) : (
                <div className="flex items-center justify-center gap-3 sm:gap-4">
                  <CountdownCell value={countdown.days} label="Days" />
                  <CountdownCell value={countdown.hours} label="Hours" />
                  <CountdownCell value={countdown.minutes} label="Minutes" />
                  <CountdownCell value={countdown.seconds} label="Seconds" />
                </div>
              )}
              <p className="mt-3 text-xs uppercase tracking-widest text-white/50 font-display">Counting down to launch</p>
            </div>
          )}

          {celebration.showBrandBirthday && celebration.brandBirthdayLabel && (
            <div className="inline-flex items-center gap-2 rounded-full border border-yellow-300/30 bg-yellow-300/10 px-4 py-1.5 text-sm font-display font-bold text-yellow-200 mt-2">
              <Sparkles size={15} /> {celebration.brandBirthdayLabel}
            </div>
          )}
        </div>

        {/* Signup */}
        <div className="mt-12 max-w-xl mx-auto rounded-3xl border border-white/12 bg-white/[0.04] backdrop-blur p-6 sm:p-8">
          <h2 className="font-display font-bold text-2xl text-center mb-1">Reserve your early access</h2>
          <p className="text-center text-white/60 text-sm mb-6">Vetted early-access list. We review requests before sending secure invite links.</p>

          {status?.type === 'success' ? (
            <div className="text-center py-6">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-green-400/15 border border-green-400/30">
                <CheckCircle2 size={30} className="text-green-300" />
              </div>
              <h3 className="font-display font-bold text-xl mb-2">You&rsquo;re on the list!</h3>
              <p className="text-white/70 text-sm max-w-sm mx-auto">{status.message}</p>
            </div>
          ) : (
            <>
              {status?.type === 'error' && (
                <div className="mb-5 rounded-2xl border px-4 py-3 text-sm border-red-400/30 bg-red-400/10 text-red-100">
                  {status.message}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input className="form-input bg-white/[0.06] border-white/15 text-white placeholder-white/40" placeholder="First name" value={form.firstName} onChange={(e) => update('firstName', e.target.value)} required />
                  <input className="form-input bg-white/[0.06] border-white/15 text-white placeholder-white/40" placeholder="Last name" value={form.lastName} onChange={(e) => update('lastName', e.target.value)} required />
                </div>
                <input type="email" className="form-input bg-white/[0.06] border-white/15 text-white placeholder-white/40" placeholder="Email address" value={form.email} onChange={(e) => update('email', e.target.value)} required />

                {/* honeypot */}
                <input className="hidden" tabIndex="-1" autoComplete="off" value={form.website} onChange={(e) => update('website', e.target.value)} aria-hidden="true" />

                <label className="flex gap-3 text-sm text-white/70">
                  <input type="checkbox" checked={form.educationAcknowledged} onChange={(e) => update('educationAcknowledged', e.target.checked)} required />
                  I understand Bond Mentor is an educational market-awareness tool, not personal financial advice.
                </label>
                <label className="flex gap-3 text-sm text-white/70">
                  <input type="checkbox" checked={form.marketingConsent} onChange={(e) => update('marketingConsent', e.target.checked)} />
                  Send me Bond Mentor launch and early-access updates.
                </label>

                <button type="submit" disabled={submitting} className="btn-primary w-full justify-center disabled:opacity-60">
                  {submitting ? 'Submitting…' : 'Join the Early-Access List'}
                </button>
              </form>
            </>
          )}

          <div className="mt-6 grid grid-cols-3 gap-3 text-center">
            <div className="text-white/60"><ShieldCheck size={18} className="mx-auto mb-1 text-blue-300" /><span className="text-[11px]">Controlled access</span></div>
            <div className="text-white/60"><Clock size={18} className="mx-auto mb-1 text-white/70" /><span className="text-[11px]">Reviewed signups</span></div>
            <div className="text-white/60"><CheckCircle2 size={18} className="mx-auto mb-1 text-red-300" /><span className="text-[11px]">Educational only</span></div>
          </div>
        </div>

        <div className="text-center mt-8">
          <button type="button" onClick={onSignIn} className="text-sm text-white/55 hover:text-white underline-offset-4 hover:underline">
            Already a member? Sign in
          </button>
        </div>
      </div>
    </div>
  );
}
